import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '../@commons/ui-components/shared.module';
import { fadeIn } from '../@commons/animations/animations';
import { HeaderPaddingSafeAreaComponent } from "../@commons/@components/header-padding-safe-area/header-padding-safe-area.component";
import { CalendarCultivationHierarchyRequest } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/cultivation/calendar-cultivation-hierarchy-list.request';
import { CalendarCultivationListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/calendar-cultivation-list.response';
import { CalendarCultivationHierarchyListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/calendar-cultivation-hierarchy-list.response';
import { CultivationVariantListResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/cultivation-variant-list.response';
import { PlantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/plant/plant-api';
import { CultivationHierarchyApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-hierarchy/cultivation-hierarchy-api';
import { StorageService } from '../@commons/@services/front/storage.service';
import { CalendarHierarchiesComponent } from './@components/calendar-hierarchies/calendar-hierarchies.component';
import { CultivationGuideToastComponent } from '../garden/@toasts/cultivation-guide-toast/cultivation-guide-toast.component';
import { CultivationApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-api';
import { CultivationResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/cultivation.response';
import { CultivationVariantApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/cultivation/cultivation-variant/cultivation-variant-api';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { PlantIconComponent } from '../@icons/plant-icon.component';
import { SortIconComponent } from '../@icons/sort-icon.component';
import { CalendarSortToastComponent } from './@components/calendar-sort-toast/calendar-sort-toast.component';
import { ClimateBannerComponent } from './@components/climate-banner/climate-banner.component';
import { ClimateInfoToastComponent } from './@toasts/climate-info-toast/climate-info-toast.component';
import { ClimatePickerToastComponent, ClimateOption } from './@toasts/climate-picker-toast/climate-picker-toast.component';
import { ClimateApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/climate/climate.api';

@Component({
  selector: 'vw-calendar',
  imports: [
    CommonModule, SharedModule, TranslateModule, HeaderPaddingSafeAreaComponent,
    CalendarHierarchiesComponent, CultivationGuideToastComponent,
    PlantIconComponent, SortIconComponent, CalendarSortToastComponent,
    ClimateBannerComponent, ClimateInfoToastComponent, ClimatePickerToastComponent
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  animations: [fadeIn],
  providers: [
    { provide: CultivationHierarchyApi, useClass: CultivationHierarchyApi },
    { provide: PlantApi, useClass: PlantApi },
    { provide: CultivationApi, useClass: CultivationApi },
    { provide: CultivationVariantApi, useClass: CultivationVariantApi },
    { provide: ClimateApi, useClass: ClimateApi },
  ]
})
export class CalendarComponent implements OnInit {
  isLoading = false;
  currentMonthCalendarItems: CalendarCultivationHierarchyListResponse[] = [];
  isShowingGuide = false;
  selectedCultivation: CultivationResponse;
  selectedVariant: CultivationVariantListResponse;
  isSortingModalOpen = false;
  sort: 'name' | 'date' | undefined = undefined;
  sortDirection: 'asc' | 'desc' | undefined = undefined;

  // Climate state
  selectedClimateType: string | null = null;
  myClimateType: string | null = null;
  isClimateInfoOpen = false;
  isClimatePickerOpen = false;

  availableClimates: ClimateOption[] = [];

  constructor(
    private cultivationHierarchyApi: CultivationHierarchyApi,
    private cultivationApi: CultivationApi,
    private cultivationVariantApi: CultivationVariantApi,
    private climateApi: ClimateApi,
    private storageService: StorageService) { }

  async ngOnInit() {
    this.setupSearchDebounce();
    await Promise.all([this.loadCalendar(), this.loadAvailableClimates(), this.loadMyClimate()]);
  }

  async loadAvailableClimates() {
    try {
      this.availableClimates = await this.climateApi.listAvailable();
    } catch (e) {
      // silent — climate picker is non-blocking
    }
  }

  async loadMyClimate() {
    try {
      const addressUid = this.storageService.getItem('address-uid');
      if (addressUid) {
        const myClimate = await this.climateApi.getMyClimate(addressUid);
        if (myClimate?.climateType) {
          this.myClimateType = myClimate.climateType;
        }
      }
    } catch (e) {
      // silent — falls back to generic "Votre climat" label
    }
  }

  async loadCalendar() {
    this.isLoading = true;
    const calendarRequest: CalendarCultivationHierarchyRequest = new CalendarCultivationHierarchyRequest();
    calendarRequest.addressUid = this.storageService.getItem('address-uid');
    if (this.selectedClimateType) {
      (calendarRequest as any).climateType = this.selectedClimateType;
    }
    this.currentMonthCalendarItems = await this.cultivationHierarchyApi.calendar(calendarRequest, this.storageService.getCurrentLanguage());
    this.isLoading = false;
  }

  onClimateSelected(type: string | null) {
    this.selectedClimateType = type;
    this.loadCalendar();
  }

  async onCultivationSelected(cultivation: CalendarCultivationListResponse) {
    const cultivationResponse = await this.cultivationApi.find(cultivation.uid, this.storageService.getCurrentLanguage());
    this.selectedCultivation = cultivationResponse;
    this.isShowingGuide = true;
    const variantResponse = await this.cultivationVariantApi.list({ cultivationUid: cultivation.uid }, 0, 1, this.storageService.getCurrentLanguage());
    this.selectedVariant = variantResponse.results[0];
  }

  private searchSubject = new Subject<string>();

  private setupSearchDebounce() {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(async (searchValue) => {
      this.isLoading = true;
      const calendarRequest = new CalendarCultivationHierarchyRequest();
      calendarRequest.addressUid = this.storageService.getItem('address-uid');
      if (this.selectedClimateType) {
        (calendarRequest as any).climateType = this.selectedClimateType;
      }
      (calendarRequest as any).name = searchValue;
      this.currentMonthCalendarItems = await this.cultivationHierarchyApi.calendar(
        calendarRequest,
        this.storageService.getCurrentLanguage()
      );
      this.isLoading = false;
    });
  }

  onSearch(searchValue: string) {
    this.searchSubject.next(searchValue);
  }

  onSortChange(sort: 'name' | 'date', sortDirection: 'asc' | 'desc') {
    this.sort = sort;
    this.sortDirection = sortDirection;
    this.currentMonthCalendarItems = this.currentMonthCalendarItems.sort((a, b) => {
      if (sort === 'name') {
        return sortDirection === 'asc' ? (a.name as string).localeCompare(b.name as string) : (b.name as string).localeCompare(a.name as string);
      }
      if (sort === 'date') {
        const dateA = a.periods?.[0]?.startDate ? new Date(a.periods[0].startDate).getTime() : Infinity;
        const dateB = b.periods?.[0]?.startDate ? new Date(b.periods[0].startDate).getTime() : Infinity;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }
      return 0;
    });
    this.isSortingModalOpen = false;
  }
}
